import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row } from "react-bootstrap";
import FalconCloseButton from "components/common/FalconCloseButton";
import MultiSelect from "components/common/MultiSelect";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { handleFillStepData } from "features/cliams/config/claimSlice";
import WizardModal from "components/wizard/WizardModal";
import TextInput from "components/common/custom/forms/BootTextField/TextField";
import BootDatePicker from "components/common/custom/forms/BootDatePicker";
import BootCheckbox from "components/common/custom/forms/BootCheckbox";

const EditClaimHeaderModal = ({ show, onHide }) => {
  const dispatch = useDispatch();
  const {
    preauthorizationType,
    preauthorizationSubType,
    visitReasons,
    processPriority,
  } = useSelector((state) => state.preauthorization);
  const { stepFormData, viewClaimInfo } = useSelector((state) => state.claim);
  const { insuranceList, singleEligibilityData } = useSelector(
    (state) => state.eligibilityCheck,
  );
  const {
    preauthorizationEligibility,
    preauthorizationRequest,
    claimRequestData,
  } = useSelector((state) => state.preauthorization);

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    defaultValues: {
      payer: null,
      related_claim_number: "",
      type: null,
      sub_type: null,
      visit_reason: null,
      priority_type: null,
      eposide_no: "",
      online_eligibility_ref_no: "",
      offline: false,
      offline_eligibility_ref_no: "",
      offline_eligibility_date: null,
      preauthorization_ref_no: "",
      prescription_ref_no: "",
    },
  });

  const [filteredSubTypes, setFilteredSubTypes] = useState([]);
  const [showTypeAlert, setShowTypeAlert] = useState(false);
  const [showPayerAlert, setShowPayerAlert] = useState(false);
  const [menuStates, setMenuStates] = useState({
    type: false,
    subType: false,
  });

  const watchType = watch("type");
  const watchOffline = watch("offline");
  const watchVisitReason = watch("visit_reason");
  const watchPayer = watch("payer");

  // Initialize form with stepFormData and eligibility data
  useEffect(() => {
    const claimRequest =
      claimRequestData?.entry?.find(
        (item) => item?.resource?.resourceType === "Claim",
      ) ||
      preauthorizationRequest?.entry?.find(
        (item) => item?.resource?.resourceType === "Claim",
      );
    const coverageEligibility = preauthorizationEligibility?.entry?.find(
      (item) => item?.resource?.resourceType === "CoverageEligibilityResponse",
    )?.resource;
    const eligibilityID = coverageEligibility?.request?.identifier?.value;

    reset({
      payer: stepFormData?.payer || null,
      related_claim_number: stepFormData?.claim_id || "STS_clmvn_7",
      type: stepFormData?.claim_type || null,
      sub_type: stepFormData?.claim_sub_type || null,
      visit_reason: stepFormData?.visit_reason || null,
      priority_type: stepFormData?.priority_type || null,
      eposide_no: stepFormData?.other_fields?.eposide_no || "",
      online_eligibility_ref_no: stepFormData?.online_eligibility_ref_no || "",
      offline: stepFormData?.offline || false,
      offline_eligibility_ref_no:
        stepFormData?.offline_eligibility_ref_no || "",
      offline_eligibility_date: stepFormData?.offline_eligibility_date || null,
      preauthorization_ref_no: stepFormData?.preauthorization_ref_no || "",
      prescription_ref_no:
        stepFormData?.prescription_ref_no ||
        (claimRequest?.resource?.use === "predetermination"
          ? claimRequest?.resource?.identifier?.value
          : ""),
      eposide_no: stepFormData?.eposide_no,
    });
  }, [
    stepFormData,
    reset,
    singleEligibilityData,
    preauthorizationEligibility,
    claimRequestData,
    preauthorizationRequest,
  ]);

  // Filter sub types based on selected type
  useEffect(() => {
    const selectedType = watchType?.claim_types_name;
    if (!selectedType) {
      setFilteredSubTypes([]);
      setValue("sub_type", null);
      return;
    }

    let updatedSubTypes = [];
    if (selectedType === "Institutional") {
      updatedSubTypes = preauthorizationSubType.filter(
        (sub) => sub.claimsub_types_name === "InPatient",
      );
    } else if (selectedType === "Professional") {
      updatedSubTypes = preauthorizationSubType.filter((sub) =>
        ["OutPatient", "Emergency"].includes(sub.claimsub_types_name),
      );
    } else {
      updatedSubTypes = preauthorizationSubType.filter(
        (sub) => sub.claimsub_types_name === "OutPatient",
      );
    }

    setFilteredSubTypes(updatedSubTypes);

    if (updatedSubTypes.length === 1) {
      setValue("sub_type", updatedSubTypes[0]);
    } else if (
      watch("sub_type") &&
      !updatedSubTypes.some(
        (sub) =>
          sub?.claimsub_types_id === watch("sub_type")?.claimsub_types_id,
      )
    ) {
      setValue("sub_type", null);
    }
  }, [watchType, preauthorizationSubType, setValue, watch]);

  // const handleChangeType = (selected) => {

  //   dispatch(handleFillStepData({ check: "discharge_date", value: null }));
  //   dispatch(handleFillStepData({ check: "triage_date", value: null }));
  //   dispatch(handleFillStepData({ check: "triage_category", value: null }));
  //   dispatch(handleFillStepData({ check: "encounter_class", value: "" }));
  //   dispatch(handleFillStepData({ check: "encounter_status", value: null }));
  //   dispatch(
  //     handleFillStepData({ check: "discharge_speciality", value: null })
  //   );
  //   dispatch(
  //     handleFillStepData({ check: "admission_speciality", value: null })
  //   );
  //   dispatch(handleFillStepData({ check: "care_type", value: null }));
  //   dispatch(
  //     handleFillStepData({ check: "encounter_admitsource", value: null })
  //   );
  //   dispatch(handleFillStepData({ check: "service_event_type", value: null }));
  //   dispatch(
  //     handleFillStepData({ check: "emer_department_disposition", value: null })
  //   );
  //   dispatch(handleFillStepData({ check: "intend_length_stay", value: null }));
  //   dispatch(handleFillStepData({ check: "cause_of_death", value: null }));
  //   dispatch(
  //     handleFillStepData({ check: "emergncy_arrival_code", value: null })
  //   );
  //   dispatch(handleFillStepData({ check: "last_menstrual_code", value: null }));
  //   dispatch(
  //     handleFillStepData({ check: "estimated_length_stay", value: null })
  //   );
  //   dispatch(handleFillStepData({ check: "act_priority", value: null }));
  // };

  // Handle form submission
  const onSubmit = (data) => {
    const currentOtherFields = stepFormData?.other_fields || {};
    dispatch(handleFillStepData({ check: "payer", value: data.payer }));
    dispatch(
      handleFillStepData({
        check: "claim_id",
        value: data.related_claim_number,
      }),
    );
    dispatch(handleFillStepData({ check: "claim_type", value: data.type }));
    dispatch(
      handleFillStepData({ check: "claim_sub_type", value: data.sub_type }),
    );
    dispatch(
      handleFillStepData({ check: "visit_reason", value: data.visit_reason }),
    );
    dispatch(
      handleFillStepData({ check: "priority_type", value: data.priority_type }),
    );
    dispatch(
      handleFillStepData({
        check: "other_fields",
        value: { ...currentOtherFields, eposide_no: data.eposide_no },
      }),
    );
    dispatch(
      handleFillStepData({
        check: "eliginility_ref_no",
        value: data.online_eligibility_ref_no,
      }),
    );
    dispatch(handleFillStepData({ check: "offline", value: data.offline }));
    dispatch(
      handleFillStepData({
        check: "offline_eligibility_ref_no",
        value: data.offline_eligibility_ref_no,
      }),
    );
    dispatch(
      handleFillStepData({
        check: "offline_eligibility_date",
        value: data.offline_eligibility_date,
      }),
    );
    dispatch(
      handleFillStepData({
        check: "eposide_no",
        value: data.eposide_no,
      }),
    );
    dispatch(
      handleFillStepData({
        check: "preauthorization_ref_no",
        value: data.preauthorization_ref_no,
      }),
    );
    dispatch(
      handleFillStepData({
        check: "prescription_ref_no",
        value: data.prescription_ref_no,
      }),
    );
    dispatch(
      handleFillStepData({
        check: "online_eligibility_ref_no",
        value: data.online_eligibility_ref_no,
      }),
    );
    onHide();
  };

  const handleTypeMenuToggle = (isOpen) => {
    if (isOpen && !watchPayer) {
      setShowPayerAlert(true);
      return setMenuStates((prev) => ({ ...prev, type: false }));
    }
    setMenuStates((prev) => ({ ...prev, type: isOpen }));
  };

  const handleSubTypeMenuToggle = (isOpen) => {
    if (isOpen && !watchType) {
      setShowTypeAlert(true);
      return setMenuStates((prev) => ({ ...prev, subType: false }));
    }
    setMenuStates((prev) => ({ ...prev, subType: isOpen }));
  };

  const isReferral = watchVisitReason?.visitreas_code === "referral";
  const isNCHI =
    watchPayer?.insurance_company_id === 41 || watchPayer?.value === 41;

  return (
    <Modal show={show} size="lg" centered>
      <Modal.Header>
        <Modal.Title className="fs-11 text-800 ">Edit Claim Header</Modal.Title>
        <FalconCloseButton
          onClick={() => {
            onHide();
          }}
          size="sm"
          className="position-absolute top-0 end-0 me-2 mt-2"
        />
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Row>
            <Col xs={12} sm={6} md={4}>
              <Form.Group controlId="payer">
                <Form.Label className=" text-800 ">
                  Payer <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="payer"
                  control={control}
                  rules={{ required: "Payer is required" }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      options={insuranceList?.map((payer) => ({
                        value: payer.insurance_company_id,
                        label:
                          payer.insurance_direct === 1
                            ? payer.payer_name
                            : `${payer.payer_name} - ${payer.insurance_shname}`,
                        original: payer,
                      }))}
                      placeholder="Choose..."
                      isClearable={true}
                      onChange={(selected) => {
                        setValue(
                          "payer",
                          selected
                            ? {
                                payer_name: selected.original.payer_name,
                                insurance_company_id:
                                  selected.original.insurance_company_id,
                                insurance_direct:
                                  selected.original.insurance_direct,
                                insurance_shname:
                                  selected.original.insurance_shname,
                              }
                            : null,
                        );
                      }}
                      value={
                        field.value
                          ? {
                              value: field.value.insurance_company_id,
                              label:
                                field.value.insurance_direct === 1
                                  ? field.value.payer_name
                                  : `${field.value.payer_name} - ${field.value.insurance_shname}`,
                            }
                          : null
                      }
                    />
                  )}
                />
                {errors.payer && (
                  <Form.Text className="text-danger  text-600 fw-normal">
                    {errors.payer.message}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Form.Group controlId="visit_reason">
                <Form.Label className=" text-800 ">
                  Visit Reason{" "}
                  {!isNCHI && <span className="text-danger">*</span>}
                </Form.Label>
                <Controller
                  name="visit_reason"
                  control={control}
                  rules={{ required: !isNCHI && "Visit Reason is required" }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      options={visitReasons?.map((visit) => ({
                        value: visit.visitreas_code,
                        label: visit.visitreas_name,
                      }))}
                      placeholder="Choose..."
                      isClearable={true}
                      onChange={(selected) => {
                        setValue(
                          "visit_reason",
                          selected
                            ? {
                                visitreas_code: selected.value,
                                visitreas_name: selected.label,
                              }
                            : null,
                        );
                      }}
                      value={
                        field.value
                          ? {
                              value: field.value.visitreas_code,
                              label: field.value.visitreas_name,
                            }
                          : null
                      }
                    />
                  )}
                />
                {errors.visit_reason && (
                  <Form.Text className="text-danger  text-600 fw-normal">
                    {errors.visit_reason.message}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Form.Group controlId="type">
                <Form.Label className=" text-800 ">
                  Type <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="type"
                  control={control}
                  rules={{ required: "Type is required" }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      options={preauthorizationType?.map((item) => ({
                        value: item.claim_types_id,
                        label: item.claim_types_name,
                        code: item?.claim_types_code,
                      }))}
                      placeholder="Choose..."
                      menuIsOpen={menuStates.type}
                      isClearable={true}
                      disabled={true}
                      onMenuOpen={() => handleTypeMenuToggle(true)}
                      onMenuClose={() => handleTypeMenuToggle(false)}
                      onChange={(selected) => {
                        setValue(
                          "type",
                          selected
                            ? {
                                claim_types_id: selected.value,
                                claim_types_name: selected.label,
                                claim_types_code: selected?.code,
                              }
                            : null,
                        );
                      }}
                      value={
                        field.value
                          ? {
                              value: field.value.claim_types_id,
                              label: field.value.claim_types_name,
                              code: field.value.claim_types_code,
                            }
                          : null
                      }
                    />
                  )}
                />
                {errors.type && (
                  <Form.Text className="text-danger  text-600 fw-normal">
                    {errors.type.message}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Form.Group controlId="sub_type">
                <Form.Label className=" text-800 ">
                  Sub Type <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="sub_type"
                  control={control}
                  rules={{ required: "Sub Type is required" }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      options={filteredSubTypes.map((item) => ({
                        value: item.claimsub_types_id,
                        label: item.claimsub_types_name,
                        code: item?.claimsub_types_code,
                      }))}
                      placeholder="Choose..."
                      menuIsOpen={menuStates.subType}
                      isClearable={true}
                      onMenuOpen={() => handleSubTypeMenuToggle(true)}
                      onMenuClose={() => handleSubTypeMenuToggle(false)}
                      onChange={(selected) => {
                        setValue(
                          "sub_type",
                          selected
                            ? {
                                claimsub_types_id: selected.value,
                                claimsub_types_name: selected.label,
                                claimsub_types_code: selected.code,
                              }
                            : null,
                        );
                      }}
                      value={
                        field.value
                          ? {
                              value: field.value.claimsub_types_id,
                              label: field.value.claimsub_types_name,
                              code: field.value.claimsub_types_code,
                            }
                          : null
                      }
                    />
                  )}
                />
                {errors.sub_type && (
                  <Form.Text className="text-danger  text-600 fw-normal">
                    {errors.sub_type.message}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Form.Group controlId="priority_type">
                <Form.Label className=" text-800 ">
                  Priority Type <span className="text-danger">*</span>
                </Form.Label>
                <Controller
                  name="priority_type"
                  control={control}
                  rules={{ required: "Priority Type is required" }}
                  render={({ field }) => (
                    <MultiSelect
                      {...field}
                      options={processPriority?.map((item) => ({
                        value: item.prcspri_code,
                        label: item.prcspri_name,
                      }))}
                      placeholder="Choose..."
                      isClearable={true}
                      onChange={(selected) => {
                        setValue(
                          "priority_type",
                          selected
                            ? {
                                prcspri_code: selected.value,
                                prcspri_name: selected.label,
                              }
                            : null,
                        );
                      }}
                      value={
                        field.value
                          ? {
                              value: field.value.prcspri_code,
                              label: field.value.prcspri_name,
                            }
                          : null
                      }
                    />
                  )}
                />
                {errors.priority_type && (
                  <Form.Text className="text-danger  text-600 fw-normal">
                    {errors.priority_type.message}
                  </Form.Text>
                )}
              </Form.Group>
            </Col>
            <Col xs={12} sm={6} md={4}>
              <TextInput
                name="related_claim_number"
                label="Related Claim Number"
                placeholder="Related Claim Number"
                control={control}
                size="sm"
                required={false}
                disabled={true}
                errors={errors}
                rules={{ required: false }}
                labelClassName=" text-800 "
                inputClassName=" text-600 fw-normal"
              />
            </Col>
            <Col xs={12} sm={6} md={4}>
              <TextInput
                name="eposide_no"
                label="Episode No"
                control={control}
                size="sm"
                required={false}
                placeholder="Episode No"
                errors={errors}
                rules={{ required: false }}
                labelClassName=" text-800 "
                inputClassName=" text-600 fw-normal"
              />
            </Col>
            <Col xs={12} sm={6} md={4}>
              <Form.Group>
                <Form.Label className=" text-800 ">Claim Status</Form.Label>
                <Form.Control
                  type="text"
                  size="sm"
                  value={viewClaimInfo?.claim_status || "invalid"}
                  disabled
                  className=" text-600 fw-normal"
                />
              </Form.Group>
            </Col>
            {!watchOffline && (
              <Col xs={12} sm={6} md={4}>
                <TextInput
                  name="online_eligibility_ref_no"
                  label="Online Eligibility Ref No"
                  control={control}
                  size="sm"
                  required={false}
                  placeholder="Online Eligibility Ref No"
                  errors={errors}
                  rules={{ required: false }}
                  labelClassName=" text-800 "
                  inputClassName=" text-600 fw-normal"
                />
              </Col>
            )}
            {watchOffline && (
              <>
                <Col xs={12} sm={6} md={4}>
                  <TextInput
                    name="offline_eligibility_ref_no"
                    label="Offline Eligibility Ref No"
                    control={control}
                    size="sm"
                    required={false}
                    placeholder="Offline Eligibility Ref No"
                    errors={errors}
                    rules={{ required: false }}
                    labelClassName=" text-800 "
                    inputClassName=" text-600 fw-normal"
                  />
                </Col>
                <Col xs={12} sm={6} md={4}>
                  <BootDatePicker
                    name="offline_eligibility_date"
                    label="Offline Eligibility Date"
                    size="sm"
                    control={control}
                    rules={{
                      required:
                        watch("offline_eligibility_ref_no") &&
                        "Offline Eligibility Date is required",
                    }}
                    placeholder="dd/mm/yyyy"
                    isRequired={watch("offline_eligibility_ref_no")}
                    labelClassName=" text-800 "
                    inputClassName=" text-600 fw-normal"
                  />
                </Col>
              </>
            )}
            <Col xs={12} sm={6} md={4}>
              <Form.Group>
                <BootCheckbox
                  name="offline"
                  label="Offline"
                  control={control}
                  size="lg"
                  labelClassName=" text-800 "
                />
              </Form.Group>
            </Col>
            {isReferral && (
              <Col xs={12} sm={6} md={4}>
                <TextInput
                  name="preauthorization_ref_no"
                  label="Preauthorization Ref No"
                  control={control}
                  size="sm"
                  required={isReferral}
                  placeholder="Preauthorization Ref No"
                  errors={errors}
                  rules={{
                    required:
                      isReferral && "Preauthorization Ref No is required",
                  }}
                  labelClassName=" text-800 "
                  inputClassName=" text-600 fw-normal"
                />
              </Col>
            )}
            {watchType?.claim_types_id === 3 && (
              <Col xs={12} sm={6} md={4}>
                <TextInput
                  name="prescription_ref_no"
                  label="Prescription Ref No"
                  control={control}
                  size="sm"
                  required={false}
                  placeholder="Prescription Ref No"
                  errors={errors}
                  rules={{ required: false }}
                  labelClassName=" text-800 "
                  inputClassName=" text-600 fw-normal"
                />
              </Col>
            )}
          </Row>
          <div className="d-flex justify-content-end mt-3">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => {
                reset(stepFormData || {});
                onHide();
              }}
              className="me-2"
            >
              Cancel
            </Button>
            <Button type="submit" size="sm" variant="primary">
              Save Changes
            </Button>
          </div>
        </Form>
      </Modal.Body>
      <WizardModal
        modal={showTypeAlert}
        setModal={() => setShowTypeAlert(false)}
        title="Type Not Selected"
        description="Please select Claim Type first before choosing Sub Type."
      />
      <WizardModal
        modal={showPayerAlert}
        setModal={() => setShowPayerAlert(false)}
        title="Payer Not Selected"
        description="Please select the Payer first before choosing Claim Type."
      />
    </Modal>
  );
};

export default EditClaimHeaderModal;
